import { useSuspenseQuery } from "@tanstack/react-query";
import {
  AnalyticsScreenProvider,
  Callout,
  FloatingFilesList,
  Flex,
  Link,
  OnboardingLayout,
  Text,
} from "@wayflyer/flyui";
import { IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import { useBankConnectionLogoResolvers, useBankConnectionLogos } from "bankconnect";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getStageCompletionStatusQueryOptions } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { IncompleteStepsModal } from "../../../components/incomplete-steps-modal";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { useApplicationSubmit } from "../../../hooks/use-application-submit";
import { BankControllerProvider, useBankControllerContext } from "../hooks/bank-controller-context";
import { useBankingAccountsModals } from "../hooks/use-banking-accounts-modals";
import { useBankStatementUploads } from "../hooks/use-bank-statement-uploads";

import { AccountDetailsPanel } from "./account-details-panel/account-details-panel";
import { BankAccountsEmptyState } from "./bank-accounts-empty-state/bank-accounts-empty-state";
import { BankAccountsFilledState } from "./bank-accounts-filled-state/bank-accounts-filled-state";
import { ConnectModal } from "./connect-modal/connect-modal";
import { MESSAGES } from "./message-definitions";
import { RequirementsModal } from "./requirements-modal/requirements-modal";

const BankingAccountsPageContent = () => {
  const intl = useIntl();

  const {
    bankConnectProps,
    bankingConnections,
    basiqClient,
    countryForm,
    countryOptions,
    defaultCountry,
    isConnectionUnsupportedCountry,
    isConnecting,
    handleConnect,
    handleNext,
    ready,
    isSubmitting,
    requiredStatementMonths,
    requireConnection,
    hasBankingConnections,
    statementCount,
    isUkIncorporated,
    stepCompleted,
  } = useBankControllerContext();

  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.BANKING });
  const applicationSubmit = useApplicationSubmit({ currentPage: PAGE_TYPE.BANKING });

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: stageCompletionStatus } = useSuspenseQuery(getStageCompletionStatusQueryOptions(customerDomain));
  const tradingHistoryThresholdMonths = stageCompletionStatus.timeInBusinessThresholdMonths ?? 6;
  const isLowTimeInBusiness = stageCompletionStatus.isLowTimeInBusiness === true;

  const modals = useBankingAccountsModals();
  const statements = useBankStatementUploads({ customerDomain, requiredStatementMonths });

  const onFilesAdded: typeof statements.onFilesAdded = (files, rejectedFiles) => {
    modals.setIsFloatingFilesListOpen(true);
    statements.onFilesAdded(files, rejectedFiles);
  };

  const logoResolvers = useBankConnectionLogoResolvers({
    customerSlug: customerDomain,
    basiqClient,
  });
  const { logoLoadingStates } = useBankConnectionLogos({
    bankConnections: bankingConnections,
    resolvers: logoResolvers,
  });

  const isEmpty = !hasBankingConnections && statementCount === 0 && !statements.hasAcceptedUploadThisSession;
  const isUploadDisabledPendingConnection = requireConnection && !hasBankingConnections;

  const onContinueConnect = async () => {
    const started = await handleConnect();
    if (started) {
      modals.setIsConnectModalOpen(false);
    }
  };

  const onNext = () => {
    if (!navigationContext.nextPath) {
      applicationSubmit.submitApplication();
      return;
    }
    handleNext();
  };

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.pageTitle)}
        subtitle={intl.formatMessage(MESSAGES.pageSubtitle)}
        footer={
          isUkIncorporated &&
          stepCompleted && (
            <Text size="xs">
              <FormattedMessage
                {...MESSAGES.ukBankingSatisfiedDisclosure}
                values={{
                  nextStep: navigationContext.nextButtonLabel,
                  privacyLink: (chunks: ReactNode) => (
                    <Link url="https://www.wayflyer.com/privacy-notice" external>
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </Text>
          )
        }
        actions={{
          primary: {
            content: navigationContext.nextButtonLabel,
            onAction: onNext,
            disabled: statements.uploadsInProgress || !ready,
            loading: isSubmitting || applicationSubmit.isSubmitting,
            icon: IconArrowRight24Line,
            analyticsId: "banking-accounts-page-next-button",
          },
          // TODO: Add back button when business model can be recalculated
          // secondary: {
          //   content: navigationContext.backButtonLabel,
          //   icon: IconArrowLeft24Line,
          //   onAction: handleBack,
          //   analyticsId: "banking-accounts-page-back-button",
          // },
        }}
      >
        {isLowTimeInBusiness && (
          <Callout
            variant="warning"
            description={intl.formatMessage(MESSAGES.timeInBusinessBanner, {
              months: tradingHistoryThresholdMonths,
              b: (chunks: ReactNode) => (
                <Text element="span" fontWeight="semiBold">
                  {chunks}
                </Text>
              ),
            })}
          />
        )}
        {isEmpty ? (
          <BankAccountsEmptyState
            bankConnectProps={bankConnectProps}
            countryForm={countryForm}
            countryOptions={countryOptions}
            defaultCountry={defaultCountry}
            isConnectionUnsupportedCountry={isConnectionUnsupportedCountry}
            isConnecting={isConnecting}
            ready={ready}
            requireConnection={requireConnection}
            isUploadDisabledPendingConnection={isUploadDisabledPendingConnection}
            onConnect={handleConnect}
            onFilesAdded={onFilesAdded}
          />
        ) : (
          <BankAccountsFilledState
            bankingConnections={bankingConnections}
            extractedAccounts={statements.extractedAccounts}
            pendingOrFailedExtractions={statements.pendingOrFailedExtractions}
            logoLoadingStates={logoLoadingStates}
            onSelectAccount={modals.onSelectAccount}
            onOpenConnectModal={() => modals.setIsConnectModalOpen(true)}
            onFilesAdded={onFilesAdded}
          />
        )}

        <Flex direction="column" gap="1">
          <Text fontWeight="semiBold" size="xs">
            {intl.formatMessage(MESSAGES.requiredFromYouHeading)}
          </Text>
          <Text size="xs">
            <FormattedMessage
              {...MESSAGES.requiredFromYouBodyText}
              values={{
                startDate: statements.statementPeriodStart,
                viewMore: (
                  <Link onClick={() => modals.setIsRequirementsModalOpen(true)}>
                    {intl.formatMessage(MESSAGES.viewMore)}
                  </Link>
                ),
              }}
            />
          </Text>
        </Flex>
      </OnboardingLayout>

      <FloatingFilesList
        defaultExpanded
        isOpen={modals.isFloatingFilesListOpen}
        labels={{
          title: intl.formatMessage(MESSAGES.floatingFilesListTitle),
          failedFileMessage: intl.formatMessage(MESSAGES.uploadFailedMessage),
          cancelAction: intl.formatMessage(MESSAGES.cancelAction),
          retryAction: intl.formatMessage(MESSAGES.retryAction),
          retryAllAction: intl.formatMessage(MESSAGES.retryAllAction),
          doneAction: intl.formatMessage(MESSAGES.doneAction),
        }}
        batches={[
          {
            id: "banking-accounts-statements",
            title: intl.formatMessage(MESSAGES.floatingFilesListTitle),
            subtitle:
              statements.uploadsState.uploads.length > 0
                ? intl.formatMessage(MESSAGES.floatingFilesListProgress, {
                    done: statements.uploadsState.uploads.filter((upload) =>
                      ["complete", "failed"].includes(upload.status)
                    ).length,
                    total: statements.uploadsState.uploads.length,
                  })
                : undefined,
            uploadsState: statements.uploadsState,
            onFileRemoved: statements.onFileRemoved,
            onFileRetry: statements.onFileRetry,
            onDone: () => modals.setIsFloatingFilesListOpen(false),
          },
        ]}
      />

      <AccountDetailsPanel
        isOpen={modals.isAccountDetailsPanelOpen}
        selectedAccount={modals.selectedAccount}
        onClose={() => modals.setIsAccountDetailsPanelOpen(false)}
        logoLoadingStates={logoLoadingStates}
      />

      <ConnectModal
        isOpen={modals.isConnectModalOpen}
        onClose={() => modals.setIsConnectModalOpen(false)}
        onContinue={onContinueConnect}
        countryForm={countryForm}
        countryOptions={countryOptions}
        defaultCountry={defaultCountry}
        isConnectionUnsupportedCountry={isConnectionUnsupportedCountry}
        isConnecting={isConnecting}
      />

      <RequirementsModal
        isOpen={modals.isRequirementsModalOpen}
        onClose={() => modals.setIsRequirementsModalOpen(false)}
        requiredStatementMonths={requiredStatementMonths}
      />
    </>
  );
};

export const BankingAccountsPage = () => (
  <AnalyticsScreenProvider screen="onboarding-banking-accounts">
    <BankControllerProvider>
      <BankingAccountsPageContent />
    </BankControllerProvider>
  </AnalyticsScreenProvider>
);
